import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  BugReport as BugIcon,
  NewReleases as FeatureIcon,
  Palette as DesignIcon,
  Build as BuildIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { format, isAfter } from 'date-fns';

interface Todo {
  id: string;
  title: string;
  description?: string;
  type: 'BUG' | 'FEATURE_REQUEST' | 'REDESIGN' | 'IMPROVEMENT' | 'MAINTENANCE' | 'DOCUMENTATION' | 'TESTING' | 'DEPLOYMENT' | 'SECURITY' | 'PERFORMANCE' | 'OTHER';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status: 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'TESTING' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD';
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  dueDate?: string;
  startDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  category?: string;
  tags: string[];
  progress: number;
  notes?: string;
  relatedClaim?: {
    id: string;
    claimNumber: string;
    file_number?: string;
  };
  relatedClient?: {
    id: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
  };
  relatedInsurer?: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

interface TodoStats {
  total: number;
  completed: number;
  inProgress: number;
  overdue: number;
  highPriority: number;
  urgent: number;
  byType: Array<{ type: string; _count: number }>;
  byStatus: Array<{ status: string; _count: number }>;
  byPriority: Array<{ priority: string; _count: number }>;
}

const TodoManagement: React.FC = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [stats, setStats] = useState<TodoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [todoDialog, setTodoDialog] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [todoToDelete, setTodoToDelete] = useState<Todo | null>(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    type: '',
    search: ''
  });
  
  // Form states
  const [todoForm, setTodoForm] = useState({
    title: '',
    description: '',
    type: 'BUG' as Todo['type'],
    priority: 'MEDIUM' as Todo['priority'],
    status: 'TODO' as Todo['status'],
    dueDate: '',
    estimatedHours: '',
    category: '',
    tags: [] as string[],
    notes: '',
    progress: 0
  });

  // Fetch todos
  const fetchTodos = async () => {
    try {
      setLoading(true);
      const params = {
        page: '1',
        limit: '50',
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      };

      const response = await api.get('/todos', { params });
      setTodos(response.data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch todos');
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await api.get('/todos/stats/overview');
      setStats(response.data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Create or update todo
  const saveTodo = async () => {
    try {
      const todoData = {
        ...todoForm,
        dueDate: todoForm.dueDate ? new Date(todoForm.dueDate).toISOString() : null,
        estimatedHours: todoForm.estimatedHours ? Number(todoForm.estimatedHours) : null,
        tags: todoForm.tags
      };

      if (editingTodo) {
        await api.put(`/todos/${editingTodo.id}`, todoData);
        setSuccess('Todo updated successfully');
      } else {
        await api.post('/todos', todoData);
        setSuccess('Todo created successfully');
      }

      setTodoDialog(false);
      setEditingTodo(null);
      resetForm();
      fetchTodos();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save todo');
    }
  };

  // Delete todo
  const deleteTodo = async () => {
    if (!todoToDelete) return;

    try {
      await api.delete(`/todos/${todoToDelete.id}`);
      setSuccess('Todo deleted successfully');
      setDeleteDialog(false);
      setTodoToDelete(null);
      fetchTodos();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete todo');
    }
  };

  // Reset form
  const resetForm = () => {
    setTodoForm({
      title: '',
      description: '',
      type: 'BUG',
      priority: 'MEDIUM',
      status: 'TODO',
      dueDate: '',
      estimatedHours: '',
      category: '',
      tags: [],
      notes: '',
      progress: 0
    });
  };

  // Handle edit
  const handleEdit = (todo: Todo) => {
    setEditingTodo(todo);
    setTodoForm({
      title: todo.title,
      description: todo.description || '',
      type: todo.type,
      priority: todo.priority,
      status: todo.status,
      dueDate: todo.dueDate ? format(new Date(todo.dueDate), 'yyyy-MM-dd') : '',
      estimatedHours: todo.estimatedHours?.toString() || '',
      category: todo.category || '',
      tags: todo.tags,
      notes: todo.notes || '',
      progress: todo.progress
    });
    setTodoDialog(true);
  };

  // Handle delete
  const handleDelete = (todo: Todo) => {
    setTodoToDelete(todo);
    setDeleteDialog(true);
  };

  // Get type icon
  const getTypeIcon = (type: Todo['type']) => {
    switch (type) {
      case 'BUG': return <BugIcon />;
      case 'FEATURE_REQUEST': return <FeatureIcon />;
      case 'REDESIGN': return <DesignIcon />;
      case 'IMPROVEMENT': return <BuildIcon />;
      case 'MAINTENANCE': return <BuildIcon />;
      case 'DOCUMENTATION': return <DescriptionIcon />;
      case 'TESTING': return <AssignmentIcon />;
      case 'DEPLOYMENT': return <ScheduleIcon />;
      case 'SECURITY': return <SecurityIcon />;
      case 'PERFORMANCE': return <SpeedIcon />;
      default: return <AssignmentIcon />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority: Todo['priority']) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };

  // Get status color
  const getStatusColor = (status: Todo['status']) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'IN_PROGRESS': return 'info';
      case 'REVIEW': return 'warning';
      case 'TESTING': return 'secondary';
      case 'CANCELLED': return 'error';
      case 'ON_HOLD': return 'default';
      default: return 'default';
    }
  };

  // Check if todo is overdue
  const isOverdue = (todo: Todo) => {
    if (!todo.dueDate) return false;
    return isAfter(new Date(), new Date(todo.dueDate)) && todo.status !== 'COMPLETED';
  };

  // Load data on mount
  useEffect(() => {
    fetchTodos();
    fetchStats();
  }, []);

  // Apply filters
  useEffect(() => {
    fetchTodos();
  }, [filters]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Todo Management
      </Typography>

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ minWidth: 200 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Todos
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" color="info.main">
                {stats.inProgress}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Overdue
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.overdue}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search"
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="TODO">To Do</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="REVIEW">Review</MenuItem>
              <MenuItem value="TESTING">Testing</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
              <MenuItem value="ON_HOLD">On Hold</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority}
              onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
              label="Priority"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="URGENT">Urgent</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              label="Type"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="BUG">Bug</MenuItem>
              <MenuItem value="FEATURE_REQUEST">Feature Request</MenuItem>
              <MenuItem value="REDESIGN">Redesign</MenuItem>
              <MenuItem value="IMPROVEMENT">Improvement</MenuItem>
              <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
              <MenuItem value="DOCUMENTATION">Documentation</MenuItem>
              <MenuItem value="TESTING">Testing</MenuItem>
              <MenuItem value="DEPLOYMENT">Deployment</MenuItem>
              <MenuItem value="SECURITY">Security</MenuItem>
              <MenuItem value="PERFORMANCE">Performance</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Todo List */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Todos ({todos.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingTodo(null);
              resetForm();
              setTodoDialog(true);
            }}
          >
            Add Todo
          </Button>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {todos.map((todo) => (
            <Card 
              key={todo.id}
              sx={{ 
                border: isOverdue(todo) ? 2 : 1,
                borderColor: isOverdue(todo) ? 'error.main' : 'divider'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Tooltip title={todo.type}>
                        <Box sx={{ mr: 1 }}>
                          {getTypeIcon(todo.type)}
                        </Box>
                      </Tooltip>
                      <Typography variant="h6" sx={{ flex: 1 }}>
                        {todo.title}
                      </Typography>
                      <Chip
                        label={todo.priority}
                        color={getPriorityColor(todo.priority) as any}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={todo.status}
                        color={getStatusColor(todo.status) as any}
                        size="small"
                      />
                    </Box>
                    
                    {todo.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {todo.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      {todo.dueDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption">
                            Due: {format(new Date(todo.dueDate), 'MMM dd, yyyy')}
                          </Typography>
                        </Box>
                      )}
                      
                      {todo.estimatedHours && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption">
                            Est: {todo.estimatedHours}h
                          </Typography>
                        </Box>
                      )}

                      {todo.progress > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption">
                            {todo.progress}%
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {todo.tags.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {todo.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <IconButton onClick={() => handleEdit(todo)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(todo)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {todos.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No todos found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first todo to get started
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Todo Dialog */}
      <Dialog open={todoDialog} onClose={() => setTodoDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTodo ? 'Edit Todo' : 'Create New Todo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={todoForm.title}
              onChange={(e) => setTodoForm(prev => ({ ...prev, title: e.target.value }))}
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={todoForm.description}
              onChange={(e) => setTodoForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={todoForm.type}
                  onChange={(e) => setTodoForm(prev => ({ ...prev, type: e.target.value as Todo['type'] }))}
                  label="Type"
                >
                  <MenuItem value="BUG">Bug</MenuItem>
                  <MenuItem value="FEATURE_REQUEST">Feature Request</MenuItem>
                  <MenuItem value="REDESIGN">Redesign</MenuItem>
                  <MenuItem value="IMPROVEMENT">Improvement</MenuItem>
                  <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                  <MenuItem value="DOCUMENTATION">Documentation</MenuItem>
                  <MenuItem value="TESTING">Testing</MenuItem>
                  <MenuItem value="DEPLOYMENT">Deployment</MenuItem>
                  <MenuItem value="SECURITY">Security</MenuItem>
                  <MenuItem value="PERFORMANCE">Performance</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={todoForm.priority}
                  onChange={(e) => setTodoForm(prev => ({ ...prev, priority: e.target.value as Todo['priority'] }))}
                  label="Priority"
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="URGENT">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={todoForm.status}
                  onChange={(e) => setTodoForm(prev => ({ ...prev, status: e.target.value as Todo['status'] }))}
                  label="Status"
                >
                  <MenuItem value="TODO">To Do</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="REVIEW">Review</MenuItem>
                  <MenuItem value="TESTING">Testing</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="ON_HOLD">On Hold</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Estimated Hours"
                type="number"
                value={todoForm.estimatedHours}
                onChange={(e) => setTodoForm(prev => ({ ...prev, estimatedHours: e.target.value }))}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={todoForm.dueDate}
                onChange={(e) => setTodoForm(prev => ({ ...prev, dueDate: e.target.value }))}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                fullWidth
                label="Category"
                value={todoForm.category}
                onChange={(e) => setTodoForm(prev => ({ ...prev, category: e.target.value }))}
              />
            </Box>

            <TextField
              fullWidth
              label="Tags (comma separated)"
              value={todoForm.tags.join(', ')}
              onChange={(e) => setTodoForm(prev => ({ 
                ...prev, 
                tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
              }))}
              helperText="Enter tags separated by commas"
            />

            <TextField
              fullWidth
              label="Notes"
              value={todoForm.notes}
              onChange={(e) => setTodoForm(prev => ({ ...prev, notes: e.target.value }))}
              multiline
              rows={3}
            />

            <Box>
              <Typography variant="body2" gutterBottom>
                Progress: {todoForm.progress}%
              </Typography>
              <input
                type="range"
                min="0"
                max="100"
                value={todoForm.progress}
                onChange={(e) => setTodoForm(prev => ({ 
                  ...prev, 
                  progress: Number(e.target.value) 
                }))}
                style={{ width: '100%' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTodoDialog(false)}>
            Cancel
          </Button>
          <Button onClick={saveTodo} variant="contained">
            {editingTodo ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{todoToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            Cancel
          </Button>
          <Button onClick={deleteTodo} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TodoManagement; 